import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar } from './ui/avatar';
import {
  addMinutesToTime,
  fmtDuration,
  fmtMoney,
  STATUS_COLOR,
  STATUS_DOT,
  STATUS_LABEL,
} from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth';

interface Props {
  appointment: any;
  showDate?: boolean;
}

export const AppointmentCard = ({ appointment: a, showDate }: Props) => {
  const isViewer = useAuthStore((s) => s.master?.role) === 'viewer';
  const total =
    Number(a.work_price ?? 0) +
    Number(a.hair_price ?? 0) +
    Number(a.tangle_price ?? 0) +
    (a.extras ?? []).reduce((s: number, e: any) => s + Number(e.price ?? 0), 0);
  const end = addMinutesToTime(a.start_time, a.planned_duration_min);
  const color = a.service?.color ?? '#8b5cf6';
  const isLive = a.status === 'in_progress';
  const isMuted = ['completed', 'cancelled', 'rescheduled', 'no_show'].includes(a.status);

  return (
    <Link to={`/appointments/${a.id}`} className="block">
      <Card
        className={cn(
          'group relative overflow-hidden transition-all duration-200 active:scale-[0.99]',
          'hover:shadow-soft-md hover:-translate-y-0.5',
          isLive && 'ring-2 ring-primary/40',
          isMuted && 'opacity-70',
        )}
      >
        {/* Color bar */}
        <span
          className="absolute inset-y-3 left-0 w-1 rounded-r-full"
          style={{ background: color }}
        />

        <div className="flex items-stretch gap-3 p-3 pl-4">
          {/* Time block */}
          <div className="flex w-14 shrink-0 flex-col justify-center">
            <div className="text-base font-bold tabular leading-tight">{a.start_time}</div>
            <div className="text-[11px] tabular text-muted-foreground">
              {fmtDuration(a.planned_duration_min)}
            </div>
            {showDate && (
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                {new Date(a.date).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: 'short',
                })}
              </div>
            )}
          </div>

          <Avatar name={a.client?.name} size="md" />

          {/* Body */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-base font-semibold">{a.client?.name}</span>
              {isLive && (
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
              <span
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: color }}
              />
              <span className="truncate">{a.service?.name}</span>
              {a.strands_count ? <span>· {a.strands_count} пр.</span> : null}
              {a.hair_grams ? <span>· {a.hair_grams} г</span> : null}
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <Badge className={cn('text-[10px]', STATUS_COLOR[a.status])}>
                <span className={cn('status-dot', STATUS_DOT[a.status])} />
                {STATUS_LABEL[a.status]}
              </Badge>
              {a.master?.name && (
                <span className="text-xs text-muted-foreground">{a.master.name}</span>
              )}
            </div>
          </div>

          {/* Right column: price (hidden for viewers) */}
          <div className="flex shrink-0 flex-col items-end justify-between">
            {!isViewer && (
              <div className="text-base font-bold tabular">{fmtMoney(total)}</div>
            )}
            <ChevronRight className={cn('h-4 w-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5', isViewer && 'ml-auto self-center')} />
          </div>
        </div>
      </Card>
    </Link>
  );
};
